import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

interface SubmitBody {
  rate: number | null
  status: 'regular' | 'skipped'
  description: string | null
  appVersion: string | null
}

export interface SubmitNpsFeedbackResult {
  submit: (input: { rate: number; description?: string }) => void
  dismiss: () => void
  isSubmitting: boolean
}

/**
 * The deployed build id, which is the commit SHA the container was built from.
 * Read live from window.__APP_CONFIG__ rather than compiled in, because the
 * bundle is promoted between environments unchanged.
 *
 * Read through a local cast rather than `declare global { interface Window }`:
 * two viltrapport packages already declare `Window.__APP_CONFIG__` with their
 * own AppConfig, and TypeScript requires every merged declaration of a global
 * interface property to be structurally identical (TS2717). A third declaration
 * would be one more shape to keep in lockstep for a single optional string.
 *
 * Note this is a different format from mobile's `version+build` in the same wire
 * field — which is why the record also carries `platform`.
 */
function buildId(): string | null {
  const config = (window as { __APP_CONFIG__?: { datadog?: { version?: string } } })
    .__APP_CONFIG__
  return config?.datadog?.version ?? null
}

/**
 * Writes one `feedbacksHistory` record.
 *
 * Deliberately has no "add a comment to my last submission" operation: records
 * carry no id, so an existing entry cannot be patched. The promoter comment flow
 * buffers the score in the component and calls submit() once — see
 * ChronicleNpsPrompt. Two records would put two responses in the denominator and
 * break the 60-day cooldown, which reads the latest record.
 */
export function useSubmitNpsFeedback(): SubmitNpsFeedbackResult {
  const call = useApiFetch()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (body: SubmitBody) =>
      call<void>('/chronicle/me/nps-feedback', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // The verdict this session was built on is now stale by construction —
      // a cooldown just started.
      void queryClient.invalidateQueries({ queryKey: ['chronicle', 'nps', 'eligibility'] })
    },
    retry: false,
  })

  return {
    submit: ({ rate, description }) =>
      mutation.mutate({
        rate,
        status: 'regular',
        description: description?.trim() ? description.trim() : null,
        appVersion: buildId(),
      }),
    dismiss: () =>
      mutation.mutate({ rate: null, status: 'skipped', description: null, appVersion: buildId() }),
    isSubmitting: mutation.isPending,
  }
}
